<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('studentEnrollmentReport.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('studentEnrollmentReport.pageTitle'),
            active: true
          }
        ]"
      />
    </div>

    <div class="row">
      <!-- Left Card: Filter Form -->
      <div class="col-md-4 col-xl-3">
        <ts-card class="shadow-sm border-0 mb-4">
          <ts-card-wrapper class="p-3">
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend
                    class="font-weight-bold text-dark mb-3"
                    style="font-size: 15px;"
                  >
                    <i class="fa fa-sliders-h text-primary mr-1"></i>
                    {{ $t('studentEnrollmentReport.criteria') }}
                  </legend>

                  <div class="form-group mb-3">
                    <label
                      class="required font-weight-bold text-secondary mb-1"
                    >
                      <i class="fa fa-building text-muted mr-1"></i>
                      {{ $t('cashCollectionByCashier.branchName') }}
                    </label>
                    <a-select
                      style="width: 100%"
                      v-model.number="model.branch_id"
                      :options="branches"
                      @change="onBranchChange"
                    ></a-select>
                    <div
                      class="validate text-danger mt-1"
                      v-if="errors.has('branch_id')"
                    >
                      {{ errors.first('branch_id') }}
                    </div>
                  </div>

                  <div class="form-group mb-3">
                    <label
                      class="required font-weight-bold text-secondary mb-1"
                    >
                      <i class="fa fa-calendar-alt text-muted mr-1"></i>
                      {{ $t('studentEnrollmentReport.acYear') }}
                    </label>
                    <a-select
                      style="width: 100%"
                      v-model.number="model.ac_year_id"
                      :options="ac_year_list"
                      @change="acYearChange"
                    ></a-select>
                    <div
                      class="validate text-danger mt-1"
                      v-if="errors.has('ac_year_id')"
                    >
                      {{ errors.first('ac_year_id') }}
                    </div>
                  </div>

                  <div class="form-group mb-3">
                    <label
                      class="required font-weight-bold text-secondary mb-1"
                    >
                      <i class="fa fa-graduation-cap text-muted mr-1"></i>
                      {{ $t('studentEnrollmentReport.subProgram') }}
                    </label>
                    <a-select
                      style="width: 100%"
                      v-model.number="model.acps_id"
                      :options="sub_programs"
                      :show-search="true"
                      :filter-option="filterOption"
                      @change="onSubChange"
                      :disabled="!model.ac_year_id"
                    ></a-select>
                    <div
                      class="validate text-danger mt-1"
                      v-if="errors.has('acps_id')"
                    >
                      {{ errors.first('acps_id') }}
                    </div>
                  </div>

                  <div class="form-group mb-3">
                    <label
                      class="required font-weight-bold text-secondary mb-1"
                    >
                      <i class="fa fa-clock text-muted mr-1"></i>
                      {{ $t('studentEnrollmentReport.acTerm') }}
                    </label>
                    <a-select
                      style="width: 100%"
                      v-model.number="model.ac_term_id"
                      :show-search="true"
                      :filter-option="filterOption"
                      :title="selectedTermTitle"
                      @change="acTermChange"
                    >
                      <a-select-option
                        v-for="term in ac_terms"
                        :key="term.value"
                        :value="term.value"
                        :title="term.label"
                      >
                        <a-tooltip :title="term.label" placement="right">
                          <span>{{ term.label }}</span>
                        </a-tooltip>
                      </a-select-option>
                    </a-select>
                    <div
                      class="validate text-danger mt-1"
                      v-if="errors.has('ac_term_id')"
                    >
                      {{ errors.first('ac_term_id') }}
                    </div>
                  </div>

                  <div class="form-group mb-4">
                    <label class="font-weight-bold text-secondary mb-1">
                      <i class="fa fa-chalkboard-teacher text-muted mr-1"></i>
                      {{ $t('studentList.classOperation') }}
                    </label>
                    <a-select
                      style="width: 100%"
                      v-model="model.class_id"
                      :options="class_options"
                      :show-search="true"
                      :placeholder="$t('all') || 'All Classes'"
                      mode="multiple"
                      :filter-option="filterOption"
                    ></a-select>
                    <div
                      class="validate text-danger mt-1"
                      v-if="errors.has('class_id')"
                    >
                      {{ errors.first('class_id') }}
                    </div>
                  </div>

                  <div class="row pt-2">
                    <div class="col-6 pr-1">
                      <ts-button
                        class="btn-block"
                        outline
                        color="success"
                        @click.prevent="exportExcel"
                        :waiting="exporting"
                      >
                        <i class="fa fa-file-excel mr-1"></i>
                        {{ $t('excel') }}
                      </ts-button>
                    </div>
                    <div class="col-6 pl-1">
                      <ts-button
                        class="btn-block"
                        color="primary"
                        @click.prevent="preview"
                        :waiting="waiting"
                      >
                        <i class="fa fa-eye mr-1"></i>
                        {{ $t('preview') }}
                      </ts-button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>

      <!-- Right Card: PDF Preview Box -->
      <div class="col-md-8 col-xl-9">
        <ts-card class="shadow-sm border-0 mb-4 bg-white">
          <ts-card-wrapper class="p-3">
            <form>
              <fieldset>
                <legend
                  class="font-weight-bold text-dark mb-3"
                  style="font-size: 15px;"
                >
                  <i class="fa fa-file-pdf text-danger mr-1"></i>
                  {{ $t('studentEnrollmentReport.preview') }}
                </legend>
                <!-- No Template Notice -->
                <div
                  v-if="no_template"
                  class="d-flex flex-column align-items-center justify-content-center text-center p-5"
                  style="height: 80vh; background: #fafafa; border: 2px dashed #d2d6de; border-radius: 6px;"
                >
                  <div
                    class="rounded-circle d-flex align-items-center justify-content-center mb-3 shadow-sm"
                    style="width: 80px; height: 80px; background: #fff1f0; border: 1px solid #ffa39e;"
                  >
                    <i
                      class="fa fa-file-pdf fa-3x text-danger"
                      style="opacity: 0.85;"
                    ></i>
                  </div>
                  <h4 class="font-weight-bold text-dark mb-2">No template</h4>
                  <p
                    class="text-muted mb-0"
                    style="max-width: 400px; font-size: 13px;"
                  >
                    មិនមានទម្រង់ PDF សម្រាប់របាយការណ៍នេះនៅឡើយទេ។
                    សូមប្រើប្រាស់ប៊ូតុង
                    <strong>Excel</strong> សម្រាប់ការទាញយកទិន្នន័យ។
                  </p>
                </div>
                <ts-preview-pdf v-else v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import _, { trim } from 'lodash'

export default {
  name: 'studentEnrollmentReport',
  data() {
    return {
      loading: false,
      waiting: false,
      exporting: false,
      no_template: false,
      src: '',
      errors: new Errors(),
      sub_programs: [],
      ac_terms: [],
      ac_year_list: [],
      class_options: [],
      branches: [],
      model: {
        branch_id: null,
        acps_id: null,
        ac_term_id: null,
        ac_year_id: null,
        class_id: []
      }
    }
  },
  computed: {
    selectedTermTitle() {
      const selected = this.ac_terms.find(
        el => el.value === this.model.ac_term_id
      )
      return selected ? selected.label : ''
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    ...mapActions('reportAcademic', [
      'getBranch',
      'studentEnrollmentReport',
      'studentEnrollmentReportExcel',
      'getSubProgram',
      'getTerm',
      'getYear',
      'getClass'
    ]),
    filterOption(input, option) {
      const query = (input || '').toLowerCase()
      const title =
        (option.componentOptions &&
          option.componentOptions.propsData &&
          option.componentOptions.propsData.title) ||
        ''
      if (title && title.toLowerCase().indexOf(query) >= 0) {
        return true
      }
      const text =
        (option.componentOptions &&
          option.componentOptions.children &&
          option.componentOptions.children[0] &&
          option.componentOptions.children[0].text) ||
        ''
      return text.toLowerCase().indexOf(query) >= 0
    },
    async fetchData() {
      this.loading = true
      await this.getBranch().then(response => {
        this.branches = response.data.map(el => ({
          value: el.branch_id,
          label: el.branch_name_en
        }))
        this.model.branch_id = this.$store.state.authUser.default_branch_id
      })
      await this.getSubProgram()
        .then(response => {
          this.sub_programs = response.data.map(el => ({
            value: el.acps_id,
            label: el.acps_name_foreign
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onBranchChange() {
      this.model.ac_year_id = null
      this.model.acps_id = null
      this.model.ac_term_id = null
      this.model.class_id = []
      this.getYear({
        filter: {
          ac_status_id: '1,2,3',
          branch_id: this.model.branch_id
        },
        branch_id: [this.model.branch_id]
      })
        .then(response => {
          this.ac_year_list = _.sortBy(response.data, ['ac_status_id']).map(
            el => ({
              value: el.ac_year_id,
              label:
                el.ac_year +
                (el.ac_status_id == 1
                  ? ' ( Current ) '
                  : el.ac_status_id == 2
                  ? ' ( History ) '
                  : ' ( Next ) ')
            })
          )
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    onSubChange(acps_id) {
      this.model.ac_term_id = null
      this.model.class_id = []
      this.loading = true
      this.getTerm({
        filter: {
          acps_id: acps_id,
          ac_status_id: '1,2,3',
          ac_year_id: this.model.ac_year_id,
          branch_id: this.model.branch_id
        },
        branch_id: [this.model.branch_id]
      })
        .then(response => {
          this.ac_terms = response.data.map(el => ({
            value: el.ac_term_id,
            label:
              el.term_name +
              (el.ac_status_id == 1
                ? ' ( Current ) '
                : el.ac_status_id == 2
                ? ' ( History ) '
                : '( Next )')
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    acYearChange() {
      this.model.acps_id = null
      this.model.ac_term_id = null
      this.model.class_id = []
    },
    acTermChange(ac_term_id) {
      this.loading = true
      this.getClass({
        filter: {
          ac_term_id: ac_term_id,
          branch_id: this.model.branch_id
        },
        branch_id: [this.model.branch_id]
      })
        .then(response => {
          this.class_options = response.data.map(el => ({
            value: el.class_id,
            label: el.class_name + ' | ' + el.teacher.full_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    preview() {
      this.waiting = true
      this.src = ''
      this.errors = new Errors()
      this.studentEnrollmentReport(this.model)
        .then(response => {
          if (response && response.url) {
            this.src = response.url
            this.no_template = false
          } else {
            this.src = ''
            this.no_template = true
            this.$toasted.info(
              response && response.message ? response.message : 'No template'
            )
          }
        })
        .catch(() => {
          this.src = ''
          this.no_template = true
          this.$toasted.info('No template')
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()

      this.studentEnrollmentReportExcel(this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          try {
            let error = JSON.parse(
              String.fromCharCode.apply(null, new Uint8Array(err))
            )
            this.$toasted.error(error.message)
            this.errors = new Errors(error.errors)
          } catch (e) {
            this.$toasted.error(err.message || 'Error downloading file')
          }
        })
        .finally(() => (this.exporting = false))
    }
  },
  watch: {
    'model.branch_id': function(val) {
      if (val) {
        this.onBranchChange()
      }
    }
  }
}
</script>
